import UserDetailsForm from "@/components/user/UserDetailsForm";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useContext, useState } from "react";
import { UserContext } from "./$userId";
import {
  companyOptions,
  createUserInAccessControlSystem,
  deleteUser,
  getImageFunction,
  updateUser,
  useBuilding,
  useCompany,
  useCompanyContact,
  useUser,
  userOptions,
} from "@/api";
import { useQueryClient } from "@tanstack/react-query";
import { useRouterStore, useSnackbarStore } from "@/store/zustandStore";
import { NotificationType } from "@/types";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@app-components";
import { getBuildingId, hasCustomErrorCode, notifyApiError } from "@/lib";
import { useCompanyAdminAccess } from "@/hooks/useCompanyAdminAccess";
import { Tooltip } from "react-tooltip";
import { useMunuUserDetails } from "@/hooks/useMunuUserDetails";
import {
  ResignationModal,
  useResignationDate,
} from "@/hooks/useResignationDate";
import { usePrompt } from "@/hooks/usePrompt";
import { useCheckDefaultRolesBeforeIntegration } from "@/hooks/useCheckDefaultRolesBeforeIntegration";

export const Route = createFileRoute(
  "/_portal/company/$companyId/users/$userId/settings",
)({
  component: UserDetailsSettings,
  loader: ({ params }) => {
    const { queryClient } = useRouterStore.getState();
    const { companyId } = params;
    if (!queryClient) return;
    queryClient.ensureQueryData(userOptions(params.userId, companyId));
    queryClient.ensureQueryData(companyOptions(companyId));
  },
});

function UserDetailsSettings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { companyId } = Route.useParams();
  const { userId, userDisabled } = useContext(UserContext);
  const { data: user } = useUser(userId, companyId);
  const { data: company } = useCompany(user.buildingTenantId);
  const { data: building } = useBuilding();
  const { addNotification } = useSnackbarStore();
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGrantingAdmin, setIsGrantingAdmin] = useState(false);
  const [isIntegrating, setIsIntegrating] = useState(false);
  const { data: companyContact } = useCompanyContact(user.buildingTenantId);
  const { allowCompanyAdminAccess } = useCompanyAdminAccess(
    companyId,
    companyId,
  );
  const { isLoading: loadingMunuUser } = useMunuUserDetails(userId);
  const {
    showResignationModal,
    setShowResignationModal,
    resignationDate,
    ...resignationModalProps
  } = useResignationDate(user.userId, companyId);

  const userIsCurrPropertyManager = userId === building.propertyManagerId;
  const { checkAndProceed, isWaiting: rolesCheckWaiting } =
    useCheckDefaultRolesBeforeIntegration(
      user.buildingTenantId,
      !!building.accessControlSystemIntegration,
      !!company.accessControlSystemIntegration,
    );

  const isAcsDown =
    (building?.acsConnectivityGuardEnabled && building?.acsConnectivityLost) ??
    false;

  usePrompt({
    promptOnNavigate: false,
    isOpen: showDeletionModal,
    title: t("EDIT_USER.DELETE_MODAL.HEADER"),
    subtitle: (
      <Trans
        i18nKey="EDIT_USER.DELETE_MODAL.BODY"
        values={{
          userName: `${user.firstName} ${user.lastName}`,
          userEmail: user.email,
        }}
        components={{
          bold: (
            <strong style={{ color: "var(--wlcm-color-danger-red)" }}></strong>
          ),
          br: <br />,
        }}
      />
    ),
    confirmText: t("EDIT_USER.DELETE"),
    confirmButtonProps: { danger: true },
    onConfirm: () => {
      setShowDeletionModal(false);
      deleteUser_();
    },
    onCancel: () => setShowDeletionModal(false),
  });

  const deleteUser_ = async () => {
    setIsDeleting(true);
    try {
      const res = await deleteUser(
        user.userId,
        user.email,
        companyId,
        user.firstName,
        user.lastName,
      );
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "users"],
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "company-admins"],
      });
      addNotification(
        t(`NOTIFICATIONS.${res.data.code}`, {
          entity: `${user.firstName} ${user.lastName}`,
        }),
        NotificationType.SUCCESS,
      );
      navigate({
        to: `/company/$companyId/users`,
        params: {
          companyId,
        },
      });
    } catch (error) {
      console.error(error);
      notifyApiError(error, addNotification, t, {
        properties: {
          entity: t("USER", { count: 1 }).toLowerCase(),
        },
        fallbackKey: "UPDATE_FAILED",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const turnUserIntoAdmin = async () => {
    if (userDisabled) return;
    setIsGrantingAdmin(true);
    try {
      await updateUser(
        user.userId,
        {
          adminInCompanies: [
            ...new Set([...user.adminInCompanies, company.buildingTenantId]),
          ],
        },
        undefined,
        companyId,
      );
      addNotification(
        t("EDIT_USER.ADMIN_SUCCESS", {
          userName: `${user.firstName} ${user.lastName}`,
          companyName: company.buildingTenantName,
        }),
        NotificationType.SUCCESS,
      );
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "user", user.userId],
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "users"],
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "company-admins"],
      });
    } catch (error) {
      console.error(error);
      addNotification(
        t("EDIT_USER.ADMIN_ERROR", {
          userName: `${user.firstName} ${user.lastName}`,
        }),
      );
    } finally {
      setIsGrantingAdmin(false);
    }
  };

  const integrateUser = async () => {
    if (userDisabled) return;
    const shouldProceed = await checkAndProceed();
    if (!shouldProceed) return;
    setIsIntegrating(true);
    const profileImageUrl = await getImageFunction(user.userProfileImgUrl);
    const buildingAllowsDigitalBadges = building?.acsDigitalBadgeMode === true;
    const effectiveCompanyDigitalBadgeMode =
      buildingAllowsDigitalBadges && company.acsDigitalBadgeMode === true;
    const sendDigitalAccessEmail =
      effectiveCompanyDigitalBadgeMode &&
      company.acsSendDigitalAccessEmailOnIntegration === true;
    try {
      await createUserInAccessControlSystem(
        user.email,
        user.firstName,
        user.lastName,
        user.userId,
        company.buildingTenantId,
        company.buildingTenantName,
        effectiveCompanyDigitalBadgeMode,
        sendDigitalAccessEmail,
        user.language,
        companyContact?.name || "",
        building.propertyOwner,
        profileImageUrl,
      );
      addNotification(
        t("EDIT_USER.INTEGRATE_SUCCESS", {
          userName: `${user.firstName} ${user.lastName}`,
        }),
        NotificationType.SUCCESS,
      );

      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "user", user.userId],
      });
      await queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "users"],
      });
    } catch (error) {
      console.error(error);
      if (
        hasCustomErrorCode(error) &&
        error.response?.data.code === "ALREADY_EXISTS_IN_ACS"
      ) {
        addNotification(t("NOTIFICATIONS.ALREADY_EXISTS_IN_ACS_NOTIFICATION"));
      } else if (
        hasCustomErrorCode(error) &&
        error.response?.data.code === "INVALID_EMAIL_IN_ACS"
      ) {
        addNotification(t("NOTIFICATIONS.INVALID_EMAIL_IN_ACS"));
      } else {
        addNotification(
          t("EDIT_USER.INTEGRATE_ERROR", {
            userName: `${user.firstName} ${user.lastName}`,
          }),
        );
      }
    } finally {
      setIsIntegrating(false);
    }
  };

  if (!allowCompanyAdminAccess) {
    return null;
  }

  return (
    <>
      <UserDetailsForm user={user} adminEditMode disabled={userDisabled} />
      <div
        className="editDangerArea"
        style={{
          marginTop: "var(--wlcm-spacing-lg)",
          flexDirection: "row",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <h4 style={{ width: "100%" }}>{t("EDIT_USER.DANGER")}</h4>
        {!!(
          !userDisabled &&
          !user.adminInCompanies.includes(company.buildingTenantId)
        ) && (
          <Button
            dark
            icon="user_cogwheel"
            disabled={isDeleting || isGrantingAdmin}
            isLoading={isGrantingAdmin}
            onClick={turnUserIntoAdmin}
          >
            {t("EDIT_USER.ADMIN", { company: company.buildingTenantName })}
          </Button>
        )}
        {!!(
          !userDisabled &&
          building.accessControlSystemIntegration &&
          company.accessControlSystemIntegration &&
          !user.integratedWithAccessControlSystem
        ) && (
          <span
            data-tooltip-id="acs-integrate-tooltip"
            data-tooltip-content={
              isAcsDown ? t("ACS_CONNECTIVITY.BUTTON_TOOLTIP") : undefined
            }
          >
            <Button
              icon="settings_account_box"
              onClick={integrateUser}
              isLoading={isIntegrating || rolesCheckWaiting}
              disabled={
                isDeleting || isIntegrating || rolesCheckWaiting || isAcsDown
              }
              dark={isIntegrating || rolesCheckWaiting || isAcsDown} // dark when disabled cuz otherwize it blends in with the background
            >
              {t("EDIT_USER.INTEGRATE")}
            </Button>
            {isAcsDown && <Tooltip id="acs-integrate-tooltip" />}
          </span>
        )}
        {!user.resignationDate && !userDisabled && (
          <Button
            icon="waving_hand"
            dark
            disabled={isDeleting}
            onClick={() => setShowResignationModal(true)}
          >
            {t("EDIT_USER.RESIGNATION_DATE.SET")}
          </Button>
        )}
        <Button
          icon="delete"
          className={
            userIsCurrPropertyManager ? `delete-user-btn-${userId}` : ""
          }
          disabled={isDeleting || userIsCurrPropertyManager || loadingMunuUser}
          isLoading={isDeleting}
          danger
          onClick={() => setShowDeletionModal(true)}
        >
          {t("EDIT_USER.DELETE")}
        </Button>
        <Tooltip
          anchorSelect={`.delete-user-btn-${userId}`}
          style={{ backgroundColor: "var(--wlcm-color-danger-red)" }}
          content={t("EDIT_USER.DELETE_DISABLED_PM")}
        />
      </div>
      <ResignationModal
        open={showResignationModal}
        onClose={() => setShowResignationModal(false)}
        resignationDate={resignationDate}
        {...resignationModalProps}
      />
    </>
  );
}
